/*
  Floating add-to-cart card (snippets/iris-sticky-add.liquid).

  Two states:
    - default: the variant selected on the PDP, times the quantity stepper
               (1 unless the shopper changes it)
    - bundle:  set by the mix-and-match picker through `iris:bundle-change`
               once a tier is chosen AND every slot has a shade
  Both show one "N% OFF" badge: the saving against the compare-at price, so a
  bundle on a discounted product shows the two discounts combined.

  The button never builds a cart request itself. It submits the main product
  form, so whatever the picker's capture-phase submit handler does (items[]
  for a bundle, validation and scroll-to-picker for an incomplete one) applies
  here exactly as it does to the main button.
*/
if (!customElements.get('iris-sticky-add')) {
  customElements.define(
    'iris-sticky-add',
    class IrisStickyAdd extends HTMLElement {
      connectedCallback() {
        this.form = document.getElementById(this.dataset.formId);
        if (!this.form) { this.hidden = true; return; }

        this.priceEl = this.querySelector('[data-iris-sticky-price]');
        this.wasEl = this.querySelector('[data-iris-sticky-was]');
        this.offEl = this.querySelector('[data-iris-sticky-off]');
        this.variantEl = this.querySelector('[data-iris-sticky-variant]');
        this.imgEl = this.querySelector('[data-iris-sticky-img]');
        this.button = this.querySelector('[data-iris-sticky-add]');
        // The main button's own wording, so a pre-order template says
        // "Pre-order" here too. It is hidden, but still carries the label.
        const mainSubmit = this.form.querySelector('[type="submit"][name="add"]');
        const mainText = mainSubmit && !mainSubmit.disabled ? mainSubmit.textContent.trim() : '';
        this.addLabel = mainText || this.button.dataset.addLabel || this.button.textContent.trim();

        try {
          const json = this.querySelector('[data-iris-sticky-variants]');
          this.variants = json ? JSON.parse(json.textContent) : {};
        } catch (e) {
          this.variants = {};
        }

        // A re-rendered card picks up a bundle the picker already completed.
        const known = window.IrisBundleState;
        this.bundle = known && known.complete ? known : null;
        this.sync = this.sync.bind(this);
        this.onBundle = (e) => { this.bundle = e.detail && e.detail.complete ? e.detail : null; this.sync(); };

        this.qtyValueEl = this.querySelector('[data-iris-qty-value]');
        this.qtyBtns = Array.from(this.querySelectorAll('[data-iris-qty-step]'));
        this.qtyBtns.forEach((btn) => btn.addEventListener('click', () => this.step(parseInt(btn.dataset.irisQtyStep, 10))));

        this.button.addEventListener('click', () => {
          const submit = this.form.querySelector('[type="submit"][name="add"], [type="submit"]');
          if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit(submit || undefined);
          else if (submit) submit.click();
        });

        document.addEventListener('iris:bundle-change', this.onBundle);
        document.addEventListener('iris:engraving-change', this.sync);
        // Any shade change on the page (theme swatches, sticky selects) may
        // move the form's variant id. The value is read a frame later, after
        // the theme's own handlers have written it.
        this.onAnyChange = () => requestAnimationFrame(this.sync);
        document.addEventListener('change', this.onAnyChange);
        // The stepper's +/- buttons set the value without a change event on
        // some paths, so clicks and typing are watched too.
        document.addEventListener('input', this.onAnyChange);
        document.addEventListener('click', this.onAnyChange);
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, this.onAnyChange);
        }

        document.body.classList.add('iris-sticky-add-on');
        // The bar's height varies (long names, bundle shade lists, phone
        // layout), so it is published as --iris-bar-h for the page padding and
        // for app widgets that have to clear it.
        if (typeof ResizeObserver === 'function') {
          this.resizeObserver = new ResizeObserver(() => {
            document.documentElement.style.setProperty('--iris-bar-h', `${Math.ceil(this.offsetHeight)}px`);
          });
          this.resizeObserver.observe(this);
        }
        this.sync();
      }

      disconnectedCallback() {
        document.removeEventListener('iris:bundle-change', this.onBundle);
        document.removeEventListener('iris:engraving-change', this.sync);
        document.removeEventListener('change', this.onAnyChange);
        document.removeEventListener('input', this.onAnyChange);
        document.removeEventListener('click', this.onAnyChange);
        if (this.unsubscribe) this.unsubscribe();
        document.body.classList.remove('iris-sticky-add-on');
        if (this.resizeObserver) this.resizeObserver.disconnect();
        document.documentElement.style.removeProperty('--iris-bar-h');
      }

      money(cents) {
        try {
          if (window.theme && theme.Currency && theme.shopSettings) {
            return theme.Currency.formatMoney(cents, theme.shopSettings.moneyFormat);
          }
        } catch (e) { /* fall through */ }
        return (cents / 100).toFixed(2);
      }

      currentVariant() {
        const input = this.form.querySelector('input[name="id"]');
        return input ? this.variants[input.value] : null;
      }

      /* The main form's (hidden) stepper input. It may sit outside the
         <form> and point at it with form="", so it is looked up by that too. */
      qtyInput() {
        const id = this.form.getAttribute('id');
        return this.form.querySelector('input[name="quantity"]') ||
          document.querySelector(`input[name="quantity"][form="${id}"]`);
      }

      quantity() {
        const input = this.qtyInput();
        const n = input ? parseInt(input.value, 10) : 1;
        return n > 0 ? n : 1;
      }

      /* Writes through to the theme's input, within its min/max/step, and
         fires change so anything listening to the stepper still hears it. */
      step(dir) {
        const input = this.qtyInput();
        if (!input || input.disabled) return;
        const stepBy = parseInt(input.step, 10) || 1;
        const min = parseInt(input.min, 10) || 1;
        const max = parseInt(input.max, 10);
        let next = this.quantity() + dir * stepBy;
        if (next < min) next = min;
        if (!isNaN(max) && next > max) next = max;
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        this.sync();
      }

      /* The bundle picker's quantity tiers ({qty, pct}), largest first. Read
         from its rows so the bar and the picker share one source; a product
         without a picker has none. */
      tiers() {
        return Array.from(document.querySelectorAll('iris-bundle-picker [data-iris-tier]:not([data-kind="fixed"])'))
          .map((t) => ({ qty: parseInt(t.dataset.qty, 10) || 0, pct: parseInt(t.dataset.pct, 10) || 0 }))
          .filter((t) => t.qty > 1 && t.pct > 0)
          .sort((a, b) => b.qty - a.qty);
      }

      /* Plain quantity (no bundle chosen) still triggers the store's
         quantity-break discount, so the bar prices it the way the cart does
         (measured 2026-09-24 on lipstick, tiers 2 = 15%, 3 = 20%): as many
         groups of the largest tier as fit, then the next tier, and leftover
         units at full price -- 4 = 3 @ 20% + 1, 5 = 3 @ 20% + 2 @ 15%,
         6 = 3 + 3 @ 20%. Each group's discount is rounded on its own, as the
         cart does per group. */
      tieredTotal(unit, qty) {
        let remaining = qty;
        let total = 0;
        this.tiers().forEach((tier) => {
          const groups = Math.floor(remaining / tier.qty);
          if (!groups) return;
          const groupFull = unit * tier.qty;
          total += groups * (groupFull - Math.round((groupFull * tier.pct) / 100));
          remaining -= groups * tier.qty;
        });
        return total + remaining * unit;
      }

      /* A bundle's variants are usually this product's own. The combo picker
         sells other products from a shell product, so it sends their price
         data along with the ids. */
      bundleVariant(id) {
        return this.variants[id] || (this.bundle && this.bundle.variants && this.bundle.variants[id]) || null;
      }

      sync() {
        const v = this.currentVariant();

        let price = 0;
        let was = 0;

        if (this.bundle && this.bundle.ids) {
          // Same rounding as the picker's row price, so the two always agree.
          let full = 0;
          this.bundle.ids.forEach((id) => {
            const bv = this.bundleVariant(id);
            if (!bv) return;
            full += bv.price;
            was += Math.max(bv.compare || 0, bv.price);
          });
          price = full - Math.round((full * (this.bundle.pct || 0)) / 100);
          // The combo picker sends its own totals (per-line discounts).
          if (typeof this.bundle.total === 'number') {
            price = this.bundle.total;
            was = this.bundle.wasTotal || was;
          }
        } else if (v) {
          const qty = this.quantity();
          price = this.tieredTotal(v.price, qty);
          was = Math.max(v.compare || 0, v.price) * qty;
        }

        if (this.bundle || v) {
          this.priceEl.textContent = this.money(price);
          this.wasEl.textContent = was > price && !(this.bundle && this.bundle.hideWas) ? this.money(was) : '';
          // One badge for the whole saving against the struck-through price:
          // the product's own discount, plus the bundle's on top of it.
          const off = was > price ? Math.round(((was - price) * 100) / was) : 0;
          this.offEl.textContent = `${off}% OFF`;
          this.offEl.hidden = off <= 0;
        }

        // Shade line: the selected shade, or every shade in a complete
        // bundle. A product with only "Default Title" keeps it hidden.
        if (this.variantEl) {
          let names = [];
          if (this.bundle && this.bundle.ids) {
            names = this.bundle.ids.map((id) => this.bundleVariant(id) && this.bundleVariant(id).title).filter(Boolean);
          } else if (v && v.title) {
            names = [v.title];
          }
          names = names.filter((n) => n !== 'Default Title');
          // Engraving block's saved initials, so the shopper sees them right
          // next to the button that adds them.
          const engr = window.IrisEngravingState && window.IrisEngravingState.text;
          this.variantEl.textContent = names.join(' + ') + (engr ? ` · Initials ${engr}` : '');
          this.variantEl.hidden = !names.length && !engr;
        }

        // A combo page's pair image (iris-combo-picker.js) owns the picture.
        if (v && v.image && this.imgEl && !this.imgEl.hasAttribute('data-iris-pair-img')) {
          this.imgEl.removeAttribute('srcset');
          this.imgEl.src = v.image;
        }

        // Stepper mirrors the theme input: its value, and locked while the
        // bundle picker has disabled it.
        const input = this.qtyInput();
        const locked = !input || input.disabled;
        this.qtyValueEl.textContent = String(this.quantity());
        this.qtyBtns.forEach((btn) => { btn.disabled = locked; });
        this.querySelector('[data-iris-sticky-qty]').classList.toggle('is-locked', locked);

        const soldOut = v ? !v.available : false;
        this.button.disabled = soldOut;
        this.button.textContent = soldOut ? (window.variantStrings && variantStrings.soldOut) || 'Sold out' : this.addLabel;
      }
    }
  );
}
